import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import s from './CHeatmap.module.scss'

import { Heatmap2D } from './Heatmap2D/Heatmap2D'
import { Heatmap3D } from './Heatmap3D/Heatmap3D'

import { AdSpotCoords, heatmapVector } from 'types/heatmap'

import { useFetch } from 'hooks/useFetch'

import { useDeviceContext } from 'contexts/DeviceContext'
import { useUnityUnloadContext } from 'contexts/UnityUnloadContext'

import { adSpotsApi } from 'api/adSpots-api'
import { campaignsApi } from 'api/campaigns-api'
import { scenesApi } from 'api/scenes-api'
import { statsApi } from 'api/stats-api'

import { AdSpaceIcon } from 'assets/icons/AdSpaceIcon'
import { ListIcon } from 'assets/icons/ListIcon'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { BurgerWrapper } from 'components/UI/BurgerWrapper/BurgerWrapper'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'
import { Spinner } from 'components/UI/Spinner/Spinner'

interface Props {
  heatmapFor: 'adspace' | 'scene' | 'campaign'
}

export const CHeatmap = ({ heatmapFor }: Props) => {
  const [mode, setMode] = useState<'2d' | '3d'>('2d')
  const { deviceWidth } = useDeviceContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { id } = useParams()

  const { data: scene, loading: sceneLoading } = useFetch({
    //request for getting scene file for 3d button disable state(heatmap for scene)
    getData: () => scenesApi.getOne(+(id || 0)),
    fetchIf: heatmapFor === 'scene',
  })

  const { data: scenes, loading: scenesLoading } = useFetch({
    getData: () => (heatmapFor === 'campaign' ? campaignsApi.getScenes(+(id || 0)) : adSpotsApi.getScenes(+(id || 0))),
    fetchIf: heatmapFor !== 'scene',
  })

  const sceneOptions = useMemo(() => {
    if (scenes?.scenes?.length && mode === '2d')
      return [...scenes.scenes.map((scene) => ({ value: scene.id, label: `${scene.coords}` }))]
    if (scenes?.scenes?.length && mode === '3d')
      return [
        ...scenes.scenes.map((scene) => ({ value: scene.id, label: `${scene.coords}`, isDisabled: !scene.scene_file })),
      ]
    else return [{ value: 0, label: 'Search...' }]
  }, [scenes?.scenes?.length, mode])

  const { control, watch, setValue } = useForm({
    defaultValues: {
      sceneSelect:
        heatmapFor === 'scene' ? { value: +(id || 0), label: 'Selected scene' } : { value: 0, label: 'Search...' },
      levelSelect: { value: '', label: 'Search...' },
    },
  })

  const sceneValue = watch('sceneSelect').value

  const [precision, setPrecision] = useState(4)

  //on scene change making request for heatmap and adspaces coords
  const { data: heatmap2d, loading: heatmapLoading } = useFetch({
    getData: () =>
      statsApi.getHeatMap({
        campaign_id: heatmapFor === 'campaign' ? +(id || 0) : null,
        ad_spot_id: heatmapFor === 'adspace' ? +(id || 0) : null,
        partition_count: Math.min(2 ** precision, 10),
        precision: precision,
        scene_id: sceneValue,
      }),
    deps: [id, sceneValue, precision],
    fetchIf: !!id && !!sceneValue,
  })

  //level options
  const levelOptions = useMemo(() => {
    if (heatmap2d?.heatmap_2d)
      return [
        ...heatmap2d.heatmap_2d.parts_heatmaps_data.map((part) => ({ value: part.part_key, label: part.part_key })),
      ]
    else return [{ value: '', label: 'Search...' }]
  }, [heatmap2d?.heatmap_2d?.parts_heatmaps_data, sceneValue])

  // resets level select when scene changes
  // useEffect(() => {
  //   setValue('levelSelect', { value: 'all', label: 'All' })
  // }, [sceneValue])

  const levelValue = watch('levelSelect').value

  const [chartData, setChartData] = useState<heatmapVector[] | undefined>([])
  const [approxPoints, setApproxPoints] = useState<heatmapVector[] | undefined>([])
  const [adSpots, setAdSpots] = useState<AdSpotCoords[]>([])
  const [heatmapSizes, setHeatmapSizes] = useState<[number, number]>([8, 8])

  useEffect(() => {
    if(sceneValue !== 0) setValue('levelSelect', { value: '', label: 'Search...' })
  }, [precision, sceneValue])

  useEffect(() => {
    if (heatmap2d?.heatmap_2d) {
      setHeatmapSizes(heatmap2d?.heatmap_2d?.heatmap_sizes)
      setChartData(heatmap2d?.heatmap_2d?.parts_heatmaps_data.find((part) => part.part_key === levelValue)?.heatmap)
      setApproxPoints(
        heatmap2d?.heatmap_2d?.parts_heatmaps_data.find((part) => part.part_key === levelValue)?.approximated_points
      )
      setAdSpots(heatmap2d?.heatmap_2d?.ad_spots)
    }
  }, [JSON.stringify(heatmap2d), levelValue, sceneValue])

  //handlers
  const [disabledLayout, setDisabledLayout] = useState(false)
  const { unload } = useUnityUnloadContext()

  const handleChangeScene = async (opt: any) => {
    setValue('sceneSelect', opt)
    if (mode === '3d' && opt.value !== sceneValue) {
      setDisabledLayout(true)
      try {
        if (unload.unload) await unload.unload()
        setMode(() => '2d')
        setMode(() => '3d')
        setDisabledLayout(() => false)
      } catch (e) {
        console.error('UNLOAD ERROR', e)
      }
    }
  }

  const handleSet2dMode = async () => {
    setDisabledLayout(true)
    try {
      if (unload.unload) await unload.unload()
      setMode('2d')
      setDisabledLayout(() => false)
    } catch (e) {
      console.error('UNLOAD ERROR', e)
    }
  }
  const handleSet3dMode = () => {
    setMode('3d')
  }

  const handleGoScenes = async () => {
    setDisabledLayout(true)
    try {
      if (unload.unload) await unload.unload()
      navigate(`/publisher/scenes`)
      setDisabledLayout(() => false)
    } catch (e) {
      console.error('UNLOAD ERROR', e)
    }
  }

  const onHide = async () => {
    if (mode === '3d') {
      setDisabledLayout(true)
      try {
        if (unload.unload) await unload.unload()
        navigate(`../?${searchParams}`)
        setDisabledLayout(() => false)
      } catch (e) {
        console.error('UNLOAD ERROR', e)
      }
    } else navigate(`../?${searchParams}`)
  }

  return (
    <ModalFullScreen onHide={onHide} loading={scenesLoading} closeDisabled={disabledLayout} className={s.modal}>
      <BurgerWrapper breakpoint={1062} wrapperClassName={s.headerHeatmap}>
        {/* <div className={s.heatmapHeader}> */}
        {deviceWidth > 768 && (
          <div className={s.headerItem}>
            <label htmlFor="sceneSelect" className={s.label}>
              Choose mode:
            </label>

            <div className={s.modeSelectorWrapper}>
              <CustomButton
                variant={mode === '2d' ? 'secondary' : 'outlined'}
                onClick={handleSet2dMode}
                size={deviceWidth > 600 ? '152' : '87'}
                disabled={disabledLayout}
              >
                2D
              </CustomButton>
              {heatmapFor === 'scene' ? (
                <CustomButton
                  variant={mode === '3d' ? 'secondary' : 'outlined'}
                  onClick={handleSet3dMode}
                  size={deviceWidth > 600 ? '152' : '87'}
                  disabled={!scene?.scene_file || sceneLoading}
                >
                  3D
                </CustomButton>
              ) : (
                <CustomButton
                  variant={mode === '3d' ? 'secondary' : 'outlined'}
                  onClick={handleSet3dMode}
                  size={deviceWidth > 600 ? '152' : '87'}
                  disabled={
                    !scenes?.scenes?.length || !scenes?.scenes.find((scene) => scene.id === +sceneValue)?.scene_file
                  }
                >
                  3D
                </CustomButton>
              )}
            </div>
          </div>
        )}
        {heatmapFor !== 'scene' ? (
          <div className={s.headerItem}>
            <label htmlFor="sceneSelect" className={s.label}>
              Select a Scene:
            </label>
            <SelectControlled
              control={control}
              name="sceneSelect"
              icon={<AdSpaceIcon />}
              placeholder="Search..."
              className={s.select}
              options={sceneOptions}
              onChange={handleChangeScene}
              disabled={disabledLayout}
            />
          </div>
        ) : null}
        {mode === '2d' && (
          <div className={s.headerItem}>
            <label htmlFor="levelSelect" className={s.label}>
              Choose level:
            </label>
            <SelectControlled
              control={control}
              name="levelSelect"
              icon={<ListIcon />}
              placeholder="Search..."
              className={s.select}
              options={levelOptions}
            />
          </div>
        )}
        {mode === '3d' && disabledLayout && (
          <div className={s.heatmap3dUnload}>
            <div />
            <div className={s.unloadContent}>
              <Spinner small /> All controls temporarily disabled. Please, wait.
            </div>
          </div>
        )}
        {/* </div> */}
      </BurgerWrapper>
      {heatmapLoading ? (
        <div className={s.loaderWrapper}>
          <Spinner centered/>
        </div>
      ) : (
        <div className={s.pageContent}>
          {mode === '3d' && (
            <div
              className={s.heatmap3dWrapper}
              style={{ visibility: sceneValue ? 'visible' : 'hidden', height: sceneValue ? '100%' : '0' }}
            >
              <Heatmap3D
                heatmapFor={heatmapFor}
                sceneValue={sceneValue}
                scene={scene}
                id={id}
                scenes={scenes}
                mode={mode}
                disabledLayout={disabledLayout}
                setDisabledLayout={setDisabledLayout}
              />
            </div>
          )}

          {!scenes?.scenes?.filter((scene) => scene.scene_file)?.length && mode === '3d' && heatmapFor !== 'scene' ? (
            <div className={s.noData}>
              <p>Seems like there is no scenes with uploaded models.</p>
              <p className={s.noDataSubtitle}>
                If you don't see your own scene, you need to download it on{' '}
                <button onClick={handleGoScenes} className={s.myScenesLink} disabled={disabledLayout}>
                  My Scenes
                </button>{' '}
                page.
              </p>
            </div>
          ) : disabledLayout && !sceneValue ? (
            <Spinner centered />
          ) : (
            //@ts-ignore
            !sceneValue && mode === '3d' && <div className={s.noData}>Please, select a scene.</div>
          )}
          {mode === '2d' && (
            <div className={s.heatmap2dWrapper}>
              {sceneValue && levelValue && chartData?.length ? (
                <Heatmap2D
                  resChartData={chartData}
                  heatmapSizes={heatmapSizes}
                  adSpots={adSpots}
                  level={levelValue}
                  scene={heatmap2d?.heatmap_2d?.scene}
                  approxPoints={approxPoints || []}
                  precision={precision}
                  setPrecision={setPrecision}
                />
              ) : sceneValue && levelValue && !chartData?.length ? (
                <div className={s.noData}>There is no data to display heatmap.</div>
              ) : (
                <div className={s.noData}>Please, select a scene and scene level.</div>
              )}
            </div>
          )}
        </div>
      )}
    </ModalFullScreen>
  )
}
